import React, { useState } from 'react';
import {
  Alert,
  Snackbar,
  CircularProgress
} from '@mui/material';
import {
  Business as BusinessIcon,
  Add as AddIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { API_BASE } from '../utils/apiUtils';

const CreateOrganization = () => {
  const [orgData, setOrgData] = useState({
    name: '',
    slug: '',
    description: '',
  });

  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });
  const [errors, setErrors] = useState({});

  const showNotification = (message, severity = 'success') => {
    setNotification({ open: true, message, severity });
  };

  const validateForm = () => {
    const newErrors = {};

    if (!orgData.name.trim()) {
      newErrors.name = 'Organization name is required';
    }

    if (!orgData.slug.trim()) {
      newErrors.slug = 'Organization slug is required';
    } else if (!/^[a-z0-9-]+$/.test(orgData.slug)) {
      newErrors.slug = 'Slug can only contain lowercase letters, numbers, and hyphens';
    }

    if (!orgData.description.trim()) {
      newErrors.description = 'Description is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Auto-generate slug from name if user edits name field
    if (name === 'name') {
      const generatedSlug = value
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)+/g, '');

      setOrgData((prev) => ({
        ...prev,
        name: value,
        slug: generatedSlug,
      }));
    } else {
      setOrgData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      showNotification('Please fix the errors below', 'error');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/admin/organizations/create-org`, {
        credentials: 'include',
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(orgData),
      });

      if (response.ok) {
        const result = await response.json();
        showNotification('Organization created successfully!', 'success');
        
        // Reset form
        setOrgData({
          name: '',
          slug: '',
          description: '',
        });
        setErrors({});
      } else {
        const errorData = await response.text();
        throw new Error(errorData || 'Failed to create organization');
      }
    } catch (error) {
      console.error('Error creating organization:', error);
      showNotification(error.message || 'Failed to create organization', 'error');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setOrgData({
      name: '',
      slug: '',
      description: '',
    });
    setErrors({});
  };

  return (
    <div className="form-container">
      <div className="form-header">
        <div className="form-icon">
          <BusinessIcon />
        </div>
        <h1 className="form-title">Create Organization</h1>
        <p className="form-subtitle">
          Set up a new organization to manage users, departments, and resources
        </p>
      </div>

      <form onSubmit={handleSubmit}>
        {/* Organization Details Section */}
        <div className="form-section">
          <h2 className="form-section-title">
            <BusinessIcon className="form-section-icon" />
            Organization Details
          </h2>
          
          <div className="form-group">
            <label className="form-label form-label-required">Organization Name</label>
            <input
              type="text"
              name="name"
              className={`form-input ${errors.name ? 'form-input-error' : ''}`}
              placeholder="Enter organization name"
              value={orgData.name}
              onChange={handleChange}
            />
            {errors.name && <span className="form-error">{errors.name}</span>}
            <span className="form-help">This will be displayed as the main organization name</span>
          </div>

          <div className="form-group">
            <label className="form-label form-label-required">Organization Slug</label>
            <input
              type="text"
              name="slug"
              className={`form-input ${errors.slug ? 'form-input-error' : ''}`}
              placeholder="organization-slug"
              value={orgData.slug}
              onChange={handleChange}
            />
            {errors.slug && <span className="form-error">{errors.slug}</span>}
            <span className="form-help">URL-friendly identifier (auto-generated from name)</span>
          </div>

          <div className="form-group">
            <label className="form-label form-label-required">Description</label>
            <textarea
              name="description"
              className={`form-input form-textarea ${errors.description ? 'form-input-error' : ''}`}
              placeholder="Describe the organization's purpose and activities"
              value={orgData.description}
              onChange={handleChange}
              rows={4}
            />
            {errors.description && <span className="form-error">{errors.description}</span>}
            <span className="form-help">Provide a brief description of the organization</span>
          </div>
        </div>

        {/* Form Actions */}
        <div className="form-actions">
          <button
            type="button"
            className="form-button form-button-secondary"
            onClick={resetForm}
            disabled={loading}
          >
            <RefreshIcon style={{ width: '16px', height: '16px' }} />
            Reset Form
          </button>
          
          <button
            type="submit"
            className="form-button form-button-primary"
            disabled={loading}
          >
            {loading ? (
              <>
                <CircularProgress size={16} color="inherit" />
                Creating...
              </>
            ) : (
              <>
                <AddIcon style={{ width: '16px', height: '16px' }} />
                Create Organization
              </>
            )}
          </button>
        </div>
      </form>

      {/* Notifications */}
      <Snackbar
        open={notification.open}
        autoHideDuration={4000}
        onClose={() => setNotification(prev => ({ ...prev, open: false }))}
      >
        <Alert 
          severity={notification.severity}
          onClose={() => setNotification(prev => ({ ...prev, open: false }))}
          icon={notification.severity === 'success' ? <CheckIcon /> : <ErrorIcon />}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default CreateOrganization;